import { DateTime, DateTimeOptions, Duration, Settings as LuxonSettings, ToISOTimeOptions } from "luxon";

const LocalDateWithTime: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",

    day: "numeric",
    month: "long",
    year: "numeric",

    timeZoneName: "short",
};

const LocalDateWithTimeNumeric: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",

    day: "numeric",
    month: "numeric",
    year: "numeric",

    timeZoneName: "short",
};

const LocalDateWithTimeNumericSeconds: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",

    day: "numeric",
    month: "numeric",
    year: "numeric",

    timeZoneName: "short"
};

const LocalDate: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric"
};

const LocalDateShortMonth: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric"
};

const LocalDateMonth: Intl.DateTimeFormatOptions = {
    month: "long",
    year: "numeric"
};

const LocalDateMonthOnly: Intl.DateTimeFormatOptions = {
    month: "long"
};

const LocalDateMonthShort: Intl.DateTimeFormatOptions = {
    month: "short",
    year: "numeric"
};

const LocalDateNoYear: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long"
};

const LocalDateNoYearShortMonth: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short"
};

const LocalDateNumeric: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric"
};

const LocalDateDoW: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    weekday: "long",
    year: "numeric",
};

const LocalDateWeekdayOnly: Intl.DateTimeFormatOptions = {
    weekday: "long",
};

const LocalDateWeekdayOnlyShort: Intl.DateTimeFormatOptions = {
    weekday: "short",
};

const LocalTime: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
};

const LocalHour: Intl.DateTimeFormatOptions = {
    hour: "numeric",
};

const LocalTime24: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    hour12: false,
    minute: "numeric",
};

export const TIME_ISO_BARE: ToISOTimeOptions = { includeOffset: false, suppressMilliseconds: true, suppressSeconds: true };

export function TZ() {
    return LuxonSettings.defaultZoneName;
}

export const TIMEZONES = [
    { id: "America/Adak", name: "America/Adak" },
    { id: "America/Anchorage", name: "America/Anchorage" },
    { id: "America/Boise", name: "America/Boise (Mountain Time)" },
    { id: "America/Chicago", name: "America/Chicago (Central Time)" },
    { id: "America/Denver", name: "America/Denver (Mountain Time)" },
    { id: "America/Detroit", name: "America/Detroit" },
    { id: "America/Indiana/Indianapolis", name: "America/Indiana/Indianapolis" },
    { id: "America/Indiana/Knox", name: "America/Indiana/Knox" },
    { id: "America/Indiana/Marengo", name: "America/Indiana/Marengo" },
    { id: "America/Indiana/Petersburg", name: "America/Indiana/Petersburg" },
    { id: "America/Indiana/Tell_City", name: "America/Indiana/Tell_City" },
    { id: "America/Indiana/Vevay", name: "America/Indiana/Vevay" },
    { id: "America/Indiana/Vincennes", name: "America/Indiana/Vincennes" },
    { id: "America/Indiana/Winamac", name: "America/Indiana/Winamac" },
    { id: "America/Juneau", name: "America/Juneau" },
    { id: "America/Kentucky/Louisville", name: "America/Kentucky/Louisville" },
    { id: "America/Kentucky/Monticello", name: "America/Kentucky/Monticello" },
    { id: "America/Los_Angeles", name: "America/Los_Angeles (Pacific Time)" },
    { id: "America/Menominee", name: "America/Menominee" },
    { id: "America/Metlakatla", name: "America/Metlakatla" },
    { id: "America/New_York", name: "America/New_York (Eastern Time)" },
    { id: "America/Nome", name: "America/Nome" },
    { id: "America/North_Dakota/Beulah", name: "America/North_Dakota/Beulah" },
    { id: "America/North_Dakota/Center", name: "America/North_Dakota/Center" },
    { id: "America/North_Dakota/New_Salem", name: "America/North_Dakota/New_Salem" },
    { id: "America/Phoenix", name: "America/Phoenix (MST)" },
    { id: "America/Sitka", name: "America/Sitka" },
    { id: "America/Yakutat", name: "America/Yakutat" },
    { id: "Pacific/Honolulu", name: "Pacific/Honolulu (Hawaii Time)" },
];

export const WEEKDAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
];

export function dateFromLocalISOString(iso: string) {
    if (!iso) {
        return undefined;
    }

    return DateTime.fromISO(iso);
}

export function formatPhone(phone: string) {
    const strip = phone.replace(/[^\d]/g, "");

    if (strip.length === 11 && strip.startsWith("1")) {
        return `+${strip.substr(0, 1)} (${strip.substr(1, 3)}) ${strip.substr(4, 3)}-${strip.substr(7, 4)}`;
    }

    return `(${strip.substr(0, 3)}) ${strip.substr(3, 3)}-${strip.substr(6, 4)}`;
}

export function formatDateTimeLocal(datetime: Date | DateTime | string) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date/time";
    }

    return datetime.toLocal().toLocaleString(LocalDateWithTime);
}

export function formatDateTimeLocalNumeric(datetime: Date | DateTime | string) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date/time";
    }

    return datetime.toLocal().toLocaleString(LocalDateWithTimeNumeric);
}

export function formatDateTimeLocalNumericSeconds(datetime: Date | DateTime | string) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date/time";
    }

    return datetime.toLocaleString(LocalDateWithTimeNumericSeconds);
}

export function formatDateTimeRelative(datetime: Date | DateTime | string) {
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }
    else if (datetime === undefined) {
        return "Invalid date/time";
    }
    if (isNaN(datetime.valueOf())) {
        return "Invalid date/time";
    }

    let diff = Math.floor(-datetime.diffNow("seconds").seconds);

    if (diff < 15) {
        return "Just now";
    }
    if (diff < 60) {
        return `${diff} ${diff === 1 ? "second" : "seconds"} ago`;
    }
    diff = Math.floor(diff / 60); // To minutes
    if (diff < 60) {
        return `${diff} ${diff === 1 ? "minute" : "minutes"} ago`;
    }
    diff = Math.floor(diff / 60); // To hours
    if (diff < 13) {
        return `${diff} ${diff === 1 ? "hour" : "hours"} ago`;
    }

    // Too long ago, show full date/time
    return datetime.toLocaleString(LocalDateWithTimeNumeric);
}

export function formatDateTimeRelativeLong(datetime: Date | DateTime | string) {
    return formatDateTimeDurationLong(datetime, "ago", "Just now");
}

export function formatDateTimeDurationLong(datetime: Date | DateTime | string, suffix: string = "", zeroText?: string) {
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }
    else if (datetime === undefined) {
        return "Invalid date/time";
    }

    let diff = Math.floor(-datetime.diffNow("seconds").seconds);

    if (diff < 15 && zeroText) {
        return zeroText;
    }
    if (diff < 60) {
        return `${diff} ${diff === 1 ? "second" : "seconds"} ${suffix}`;
    }
    diff = Math.floor(diff / 60); // To minutes
    if (diff < 60) {
        return `${diff} ${diff === 1 ? "minute" : "minutes"} ${suffix}`;
    }
    diff = Math.floor(diff / 60); // To hours
    if (diff < 24) {
        return `${diff} ${diff === 1 ? "hour" : "hours"} ${suffix}`;
    }
    diff = Math.floor(diff / 24); // To days

    return `${diff} ${diff === 1 ? "day" : "days"} ${suffix}`;
}

export function compareDates(date1: Date | string, date2: Date | string) {
    if (!date1 || !date2) {
        return 0;
    }

    if (typeof date1 === "string") {
        if (date1.replace(" ", "") === "") {
            return 0;
        }

        date1 = new Date(date1);
    }
    if (typeof date2 === "string") {
        if (date2.replace(" ", "") === "") {
            return 0;
        }

        date2 = new Date(date2);
    }

    const diff = Math.floor((date1.getTime() - date2.getTime()) / 1000);

    return diff;
}

export function formatDateLocal(datetime: DateTime | string, shortMonth: boolean = false) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date";
    }

    return datetime.toLocaleString(shortMonth ? LocalDateShortMonth : LocalDate);
}

export function formatDateLocalMonth(datetime: Date | DateTime | string, noYear = false) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date";
    }

    return datetime.toLocaleString(noYear ? LocalDateMonthOnly : LocalDateMonth);
}

export function formatDateLocalMonthShort(datetime: Date | DateTime | string) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date";
    }

    return datetime.toLocaleString(LocalDateMonthShort);
}

export function formatDateLocalNumeric(datetime: Date | DateTime | string) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date";
    }

    return datetime.toLocal().toLocaleString(LocalDateNumeric);
}

export function formatDateLocalDayOfWeek(datetime: Date | DateTime | string) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date";
    }

    return datetime.toLocal().toLocaleString(LocalDateDoW);
}

export function formatDateLocalWeekdayOnly(datetime: Date | DateTime | string, short = false) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date";
    }

    return datetime.toLocal().toLocaleString(short ? LocalDateWeekdayOnlyShort : LocalDateWeekdayOnly);
}

export function formatDateLocalScale(datetime: Date | DateTime | string, short = false) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date";
    }

    const local = datetime.toLocal();

    if (-local.diffNow("seconds").seconds < 30) {
        return "Just now";
    }
    else if (-local.diffNow("days").days >= 7) {
        return datetime.toLocal().toLocaleString(short ? LocalDateShortMonth : LocalDateMonth);
    }
    else {
        return datetime.toLocal().toRelative();
    }
}

export function formatDateLocalNoYear(datetime: Date | DateTime | string, shortMonth: boolean = false) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date";
    }

    return datetime.toLocal().toLocaleString(shortMonth ? LocalDateNoYearShortMonth : LocalDateNoYear);
}

export function calculateAge(date: Date | string | undefined | null) {
    if (!date) {
        return date;
    }
    if (typeof date !== "string") {
        date = date.toISOString();
    }

    // TODO: Replace with luxon?
    const dateParts = date.substr(0, 10).split('-');
    const d = new Date(+dateParts[0], +dateParts[1] - 1, +dateParts[2]);
    const now = new Date();

    let age = now.getFullYear() - d.getFullYear();
    if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) {
        age--;
    }

    return age;
}

export function formatDateAge(date: Date | string | undefined | null) {
    return calculateAge(date)?.toString();
}

export function formatDatePartial(value: string) {
    const stripped = value.replace(/[^\d/]/g, "");

    // Match form of M to MM/DD/YYYY and all substrings between
    const match = stripped.match(/(\d{0,2})(\/?)(?:(\d{0,2})?(\/?))(?:(\d{0,4}))/);
    if (!match) {
        return "";
    }

    let result = "";

    if (match[1]) {
        const part = match[1];
        result += (match[1].length < 2 && match[2]) ? "0" + part : part;
    }
    if (match[3]) {
        result += "/" + ((match[3].length < 2 && match[4]) ? "0" + match[3] : match[3]);
    }
    if (match[5]) {
        result += "/" + match[5];
    }

    return result;
}

export function formatTimeLocal(datetime: Date | DateTime | string, is24Hour: boolean = false) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date/time";
    }

    return datetime.toLocal().toLocaleString(is24Hour ? LocalTime24 : LocalTime);
}

export function formatTimeLocalSmall(datetime: Date | DateTime | string) {
    if (!datetime) {
        return "";
    }
    if (typeof datetime === "string") {
        if (datetime.replace(" ", "") === "") {
            return "Invalid date";
        }

        datetime = DateTime.fromISO(datetime);
    }
    else if (datetime instanceof Date) {
        datetime = DateTime.fromJSDate(datetime);
    }

    if (isNaN(datetime.valueOf())) {
        return "Invalid date/time";
    }

    return datetime.toLocal().toLocaleString(datetime.minute === 0 ? LocalHour : LocalTime);
}

export function combineDateTime(date: string, time: string | null | undefined) {
    if (!date) {
        return "";
    }

    let dt = DateTime.fromISO(date, { zone: "America/New_York" })
    if (time) {
        const dur = Duration.fromISOTime(time);
        dt = dt.plus(dur);
    }

    return dt;
}

export function formatTelephonePartial(value: string) {
    const stripped = value.replace(/[^\d]/g, "");
    let test = "";
    if (stripped.length > 0) {
        test = "(" + stripped.substr(0, 3);
    }
    if (stripped.length > 3) {
        test += ") " + stripped.substr(3, 3);
    }
    if (stripped.length > 6) {
        test += "-" + stripped.substr(6, 4);
    }

    return test;
}

export function formatTelephone(value: string) {
    if (!value)
        return value;

    const stripped = value.replace(/[^\d]/g, "");

    if (stripped.length >= 11) {
        // +X (XXX) XXX-XXXX
        const ccLen = stripped.length - 10;

        return `+${stripped.substr(0, ccLen)} (${stripped.substr(-10, 3)}) ${stripped.substr(-7, 3)}-${stripped.substr(-4, 4)}`;
    }
    else if (stripped.length >= 10) {
        // (XXX) XXX-XXXX
        return `(${stripped.substr(-10, 3)}) ${stripped.substr(-7, 3)}-${stripped.substr(-4, 4)}`;
    }
    else if (stripped.length >= 7) {
        // XXX-XXXX
        return `(${stripped.substr(-10, 3)}) ${stripped.substr(-7, 3)}-${stripped.substr(-4, 4)}`;
    }

    return value;
}

// Validates NANP numbers only (country code +1)
export const validatePhoneNumberNanp = /^\((?:[2-9][0-9][0-9])\)\s(?:[2-9][0-9][0-9])-(?:[0-9]{4})/;
export const validatePhoneNumberNanpHeavy = /^\((?:[2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\)\s(?:[2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])-(?:[0-9]{4})/;
export const validatePhoneNumber = /^\(\d{3}\)\s\d{3}-\d{4}/;
export const validatePhoneNumberOptional = /^$|^\(\d{3}\)\s\d{3}-\d{4}/;
export const validateEmail = /^.+@[\w\-.]+\.\w+$/;
export const validateEmailOrNone = /^(?:none)|(?:.+@[\w\-.]+\.\w+)$/;
export const validateEmailOptional = /^$|^.+@[\w\-.]+\.\w+$/;

export function formatSocialSecurityPartial(value: string) {
    const stripped = value.replace(/[^\d]/g, "");
    let test = "";
    if (stripped.length > 0) {
        test = stripped.substr(0, 3);
    }
    if (stripped.length > 3) {
        test += "-" + stripped.substr(3, 2);
    }
    if (stripped.length > 5) {
        test += "-" + stripped.substr(5, 4);
    }

    return test;
}

export function formatSocialSecurityMasked(value: string) {
    const stripped = value.replace(/[^\d]/g, "");
    if (stripped.length < 9) {
        return "";
    }

    return "***-**-" + stripped.substr(5, 4);
}

export function formatZipCode(value: string) {
    if (!value) {
        return "";
    }
    value = value.replace(/[^0-9]/g, "");

    const match = value.match(/(^[0-9]{0,5})/g);
    return match ? match[0] : "";
}

export function filterStateCode(value: string) {
    if (!value) {
        return "";
    }

    value = value.toUpperCase();

    const match = value.match(/^((A[LKSZR]?)|(C[AOT]?)|(D[EC]?)|(F[ML]?)|(G[AU]?)|(H[I]?)|(I[DLNA]?)|(K[SY]?)|(L[A]?)|(M[EHDAINSOT]?)|(N[EVHJMYCD]?)|(M[P]?)|(O[HKR]?)|(P[WAR]?)|(R[I]?)|(S[CD]?)|(T[NX]?)|(U[T]?)|(V[TIA]?)|(W[AVIY]?))/);
    return match ? match[0] : "";
}

export function validateStateCode(value: string) {
    if (!value) {
        return false;
    }

    const match = value.match(/^((A[LKSZR])|(C[AOT])|(D[EC])|(F[ML])|(G[AU])|(HI)|(I[DLNA])|(K[SY])|(LA)|(M[EHDAINSOT])|(N[EVHJMYCD])|(MP)|(O[HKR])|(P[WAR])|(RI)|(S[CD])|(T[NX])|(UT)|(V[TIA])|(W[AVIY]))$/i);
    return !!match;
}

export function formatName(name: { title?: string, first: string, middle?: string, last: string, suffix?: string }, skipSuffix = false) {
    if (!name) {
        return "";
    }

    if (name.middle) {
        return `${name.title ? (name.title + ". ") : ""}${name.first} ${name.middle} ${name.last}${name.suffix && !skipSuffix ? (", " + name.suffix) : ""}`;
    }
    else {
        return `${name.title ? (name.title + ". ") : ""}${name.first} ${name.last}${name.suffix && !skipSuffix ? (", " + name.suffix) : ""}`;
    }
}

export function diffObjects(objA: any, objB: any) {
    const objDiff: { [key: string]: any } = {};

    for (const key of Object.keys(objA)) {
        if (typeof objA[key] === "object" && !Array.isArray(objA[key]) && objA[key] !== null && objB[key] !== null && objA[key] !== undefined && objB[key] !== undefined) {
            // Recurse
            const d = diffObjects(objA[key], objB[key]);
            if (d) {
                objDiff[key] = d;
            }
        }
        else if (Array.isArray(objA[key]) && Array.isArray(objB[key])) {
            let areDifferent = objA[key].length !== objB[key].length;
            if (!areDifferent) {
                // Compare elements
                for (let i = 0; i < objA[key].length; i++) {
                    areDifferent = (diffObjects(objA[key][i], objB[key][i]) !== undefined) || areDifferent;
                }
            }

            if (areDifferent) {
                objDiff[key] = objB[key];
            }
        }
        else if (objA[key] !== objB[key]) {
            objDiff[key] = objB[key];
        }
    }

    // Add keys in B that aren't in A
    for (const key of Object.keys(objB)) {
        if (!objA.hasOwnProperty(key)) {
            objDiff[key] = objB[key];
        }
    }

    return Object.keys(objDiff).length === 0 ? undefined : objDiff;
}

export function deepCopy<T>(objA: T): T {
    if (Array.isArray(objA)) {
        // The type casting here is to make the compiler quiet down about what we're going here
        // objA should always be an array once Array.isArray() validates it
        return deepCopyArray(objA as T[]) as any as T;
    }

    // Initial shallow copy
    const output = Object.assign({} as T, objA);

    for (const key of Object.keys(output)) {
        const value = (output as any)[key];
        if (typeof value !== "object" || value === null)
            continue;

        if (Array.isArray(value)) {
            // Copy array
            if (((output as any)[key] as any[]).some(a => typeof a === "object")) {
                // Array has complex values
                (output as any)[key] = deepCopyArray((output as any)[key] as any[]);
            }
            else {
                // Array has primitive values
                (output as any)[key] = [...value];
            }
        }
        else {
            // Copy object
            (output as any)[key] = deepCopy(value);
        }
    }

    return output;
}

export function deepCopyArray<T>(arr: T[]): T[] {
    return arr.map(a => typeof a === "object" ? deepCopy(a) : a);
}

export function truncateEllipse(str: string, length: number) {
    if (!str)
        return str;

    return str.substring(0, length) + (str.length > length ? "..." : "");
}

export function formatSize(bytes: number, places: number = 2) {
    if (bytes > 1024 * 1024 * 1024) // GiB
        return `${(bytes / 1024 / 1024 / 1024).toFixed(places)} GiB`;
    else if (bytes > 1024 * 1024) // MiB
        return `${(bytes / 1024 / 1024).toFixed(places)} MiB`;
    else if (bytes > 1024) // KiB
        return `${(bytes / 1024).toFixed(places)} KiB`;
    else
        return `${bytes} Bytes`;
}

const mimeInfo: {
    [name: string]: {
        name: string,
        shortName: string,
        defaultExtension: string
    }
} = {
    "": {
        name: "Unknown",
        shortName: "Unknown",
        defaultExtension: "bin"
    },
    "image/bmp": {
        name: "Bitmap Image",
        shortName: "BMP",
        defaultExtension: "bmp"
    },
    "image/gif": {
        name: "GIF Image",
        shortName: "GIF",
        defaultExtension: "gif"
    },
    "image/jpeg": {
        name: "JPEG Image",
        shortName: "JPEG",
        defaultExtension: "jpg"
    },
    "image/png": {
        name: "PNG Image",
        shortName: "PNG",
        defaultExtension: "png"
    },
    "image/svg+xml": {
        name: "SVG Image",
        shortName: "SVG",
        defaultExtension: "svg"
    },
    "image/tiff": {
        name: "TIFF Image",
        shortName: "TIFF",
        defaultExtension: "tif"
    },
    "image/webp": {
        name: "WEBP Image",
        shortName: "WEBP",
        defaultExtension: "webp"
    },
    "video/webm": {
        name: "WebM Video",
        shortName: "WebM",
        defaultExtension: "webm"
    },
    "audio/mpeg": {
        name: "MP3 Audio",
        shortName: "MP3",
        defaultExtension: "mp3"
    },
    "audio/ogg": {
        name: "Ogg Audio",
        shortName: "OGG",
        defaultExtension: "ogg"
    },
    "audio/webm": {
        name: "WebM Audio",
        shortName: "WebM",
        defaultExtension: "webm"
    },
    "text/html": {
        name: "HTML Document",
        shortName: "HTML",
        defaultExtension: "html"
    },
    "text/plain": {
        name: "Text Document",
        shortName: "Text",
        defaultExtension: "txt"
    },
    "application/octet-stream": {
        name: "Binary File",
        shortName: "File",
        defaultExtension: "bin"
    },
    "application/pdf": {
        name: "PDF Document",
        shortName: "PDF",
        defaultExtension: "pdf"
    },
    "application/ogg": {
        name: "OGG File",
        shortName: "OGG",
        defaultExtension: "ogg"
    },
    "application/vnd.ms-excel": {
        name: "Excel Document",
        shortName: "Excel",
        defaultExtension: "xls"
    },
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
        name: "Excel XML Document",
        shortName: "Excel XML",
        defaultExtension: "xlsx"
    }
};

mimeInfo["image/jfif"] = mimeInfo["image/jpeg"];

export function formatMimeShort(mime: string) {
    const info = mimeInfo[mime];
    if (!info) {
        return mime;
    }

    return info.shortName;
}

export function formatMimeLong(mime: string) {
    const info = mimeInfo[mime];
    if (!info) {
        return mime;
    }

    return info.name;
}

export function formatMimeFilename(filename: string, mime: string) {
    const info = mimeInfo[mime];
    if (!info) {
        return mime;
    }

    return `${filename}.${info.defaultExtension}`;
}

export function formatUSD(amount: number | string | null | undefined, wholeOnly = false) {
    if (amount === null || amount === undefined)
        return "-";

    amount = +amount;
    return isNaN(amount) ? "-" : amount.toLocaleString(undefined, { currency: "USD", style: "currency", maximumFractionDigits: wholeOnly ? 0 : undefined });
}

export function formatList(items: any[], transform?: (item: any, index: number) => any, spacer = "and") {
    if (items.length === 0)
        return "";

    if (items.length === 1)
        return transform ? transform(items[0], 0) : items[0];

    if (items.length === 2) {
        items.splice(1, 0, ` ${spacer} `);

        return transform ? items.map(transform) : items.join("");
    }

    if (transform) {
        const output = [];
        for (let i = 0; i < items.length; i++) {
            output.push(transform(items[i], i * 2));
            if (i < items.length - 2) {
                output.push(transform(", ", (i * 2) + 1));
            }
            else if (i < items.length - 1) {
                output.push(transform(`, ${spacer} `, (i * 2) + 1));
            }
        }

        return output;
    }
    else {
        return items.slice(0, -1).join(", ") + `, ${spacer} ` + items[items.length - 1];
    }
}

export function transformJoin<T>(items: T[], transform: (item: T) => string, separator: string) {
    const output = [];
    for (let i = 0; i < items.length; i++) {
        output.push(transform(items[i]));
    }

    return output.join(separator);
}

export function interleave<T>(items: T[], transform: (item: T) => any, separator: string) {
    const output = [];
    for (let i = 0; i < items.length; i++) {
        if (i > 0) {
            output.push(separator);
        }

        output.push(transform(items[i]));
    }

    return output;
}

export function compareVersions(a: string, b: string) {
    const aParts = a.split(".");
    const bParts = b.split(".");

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const diff = (+aParts[i] || 0) - (+bParts[i] || 0);
        if (diff !== 0)
            return Math.sign(diff);
    }

    return 0;
}

export function formatId(id: string) {
    let idOutput = id.substr(-5);

    let i = id.length - 10;
    for (; i >= 0; i -= 5) {
        idOutput = `${id.substr(i, 5)}-${idOutput}`;
    }
    if (i < 0) {
        idOutput = `${id.substr(0, 5 + i)}-${idOutput}`;
    }

    return idOutput;
}

export function getSetStateAsync<S>() {
    return async function setStateAsync<K extends keyof S, P = {}>(
        this: any,
        state: ((prevState: Readonly<S>, props: Readonly<P>) => (Pick<S, K> | S | null)) | (Pick<S, K> | S | null),
        callback?: () => void
    ): Promise<void> {
        if (callback) {
            return new Promise((resolve, reject) => {
                this.setState(state as any, () => {
                    resolve();
                    callback && callback();
                });
            });
        }
        else {
            return new Promise((resolve, reject) => {
                this.setState(state as any, resolve);
            });
        }
    };
}

export function parseDateSafe(text: string, options?: DateTimeOptions | undefined) {
    try {
        const dt = DateTime.fromISO(text, options);

        return dt;
    }
    catch (e) {
        return undefined;
    }
}

export function createDataUri(mime: string, data: string) {
    return `data:${mime};base64,${data}`;
}

export const COLORS = ["#3cb44b", "#e6194B", "#ffe119", "#4363d8", "#f58231", "#911eb4", "#42d4f4", "#f032e6", "#bfef45", "#fabebe", "#469990", "#e6beff", "#9A6324", "#fffac8", "#800000", "#aaffc3", "#808000", "#ffd8b1", "#000075", "#a9a9a9", "#ffffff", "#000000"];
export const COLORS2 = ["#3cb44b", "#e6194B", "#4363d8", "#f58231", "#911eb4", "#42d4f4", "#f032e6", "#bfef45", "#fabebe", "#469990", "#e6beff", "#9A6324", "#800000", "#aaffc3", "#808000", "#ffd8b1", "#000075", "#a9a9a9", "#000000"];

export function angleToDirection(angle: number) {
    if (angle >= 348.75 || angle < 11.25)
        return "N";
    if (angle >= 11.25 && angle < 33.75)
        return "NNE";
    if (angle >= 33.75 && angle < 56.25)
        return "NE";
    if (angle >= 56.25 && angle < 78.75)
        return "ENE";
    if (angle >= 78.75 && angle < 101.25)
        return "E";
    if (angle >= 101.25 && angle < 123.75)
        return "ESE";
    if (angle >= 123.75 && angle < 146.25)
        return "SE";
    if (angle >= 146.25 && angle < 168.75)
        return "SSE";
    if (angle >= 168.75 && angle < 191.25)
        return "S";
    if (angle >= 191.25 && angle < 213.75)
        return "SSW";
    if (angle >= 213.75 && angle < 236.25)
        return "SW";
    if (angle >= 236.25 && angle < 258.75)
        return "WSW";
    if (angle >= 258.75 && angle < 281.25)
        return "W";
    if (angle >= 281.25 && angle < 303.75)
        return "WNW";
    if (angle >= 303.75 && angle < 326.25)
        return "NW";
    if (angle >= 326.25 && angle < 348.75)
        return "NNW";

    return "UNK";
}

export function formatNumber(number: number | undefined | null, places?: number) {
    if (number === undefined || number === null) {
        return number;
    }

    if (places !== undefined) {
        return number.toLocaleString(undefined, { minimumFractionDigits: places });
    }

    return number.toLocaleString();
}
